import React, { useCallback, useEffect } from 'react';

import { ResponsiveSizes, VideoPlayerProps } from 'features/player';
import styled from 'styled-components';

interface ISpallaEvent {
  detail: {
    duration: number;
    event: 'play' | 'pause' | 'stats' | 'ended' | 'ready';
    date: Date;
    ts: number;
    key: string;
    isLive: boolean;
    isAdsPlaying: boolean;
    fullScreen: boolean;
    paused: boolean;
    ended: boolean;
    currentTime: number;
    controlBarVisible: boolean;
  };
}

const PlayerWrapper = styled('div')<{
  width: ResponsiveSizes;
  height: ResponsiveSizes;
  maxWidth: ResponsiveSizes;
  maxHeight: ResponsiveSizes;
}>`
  position: relative;

  ${({ width, theme: { mediaQueries } }) => {
    if (typeof width === 'object') {
      let response = '';

      if (width.m) {
        response = `
          ${response}

          ${mediaQueries.mobile} {
            width: ${typeof width.m === 'number' ? `${width.m}px` : width.m};
          }
        `;
      }

      if (width.t) {
        response = `
          ${response}

          ${mediaQueries.tablet} {
            width: ${typeof width.t === 'number' ? `${width.t}px` : width.t};
          }
        `;
      }

      if (width.d) {
        response = `
          ${response}

          ${mediaQueries.desktop} {
            width: ${typeof width.d === 'number' ? `${width.d}px` : width.d};
          }
        `;
      }

      return response;
    } else {
      return `
        width: ${typeof width === 'number' ? `${width}px` : width};
      `;
    }
  }};

  ${({ height, theme: { mediaQueries } }) => {
    if (typeof height === 'object') {
      let response = '';

      if (height.m) {
        response = `
          ${response}

          ${mediaQueries.mobile} {
            height: ${typeof height.m === 'number' ? `${height.m}px` : height.m};
          }
        `;
      }

      if (height.t) {
        response = `
          ${response}

          ${mediaQueries.tablet} {
            height: ${typeof height.t === 'number' ? `${height.t}px` : height.t};
          }
        `;
      }

      if (height.d) {
        response = `
          ${response}

          ${mediaQueries.desktop} {
            height: ${typeof height.d === 'number' ? `${height.d}px` : height.d};
          }
        `;
      }

      return response;
    } else {
      return `
        height: ${typeof height === 'number' ? `${height}px` : height};
      `;
    }
  }};

  ${({ maxWidth, theme: { mediaQueries } }) => {
    if (typeof maxWidth === 'object') {
      let response = '';

      if (maxWidth.m) {
        response = `
          ${response}

          ${mediaQueries.mobile} {
            max-width: ${typeof maxWidth.m === 'number' ? `${maxWidth.m}px` : maxWidth.m};
          }
        `;
      }

      if (maxWidth.t) {
        response = `
          ${response}

          ${mediaQueries.tablet} {
            max-width: ${typeof maxWidth.t === 'number' ? `${maxWidth.t}px` : maxWidth.t};
          }
        `;
      }

      if (maxWidth.d) {
        response = `
          ${response}

          ${mediaQueries.desktop} {
            max-width: ${typeof maxWidth.d === 'number' ? `${maxWidth.d}px` : maxWidth.d};
          }
        `;
      }

      return response;
    } else {
      return `
        max-width: ${typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth};
      `;
    }
  }};

  ${({ maxHeight, theme: { mediaQueries } }) => {
    if (typeof maxHeight === 'object') {
      let response = '';

      if (maxHeight.m) {
        response = `
          ${response}

          ${mediaQueries.mobile} {
            max-height: ${typeof maxHeight.m === 'number' ? `${maxHeight.m}px` : maxHeight.m};
          }
        `;
      }

      if (maxHeight.t) {
        response = `
          ${response}

          ${mediaQueries.tablet} {
            max-height: ${typeof maxHeight.t === 'number' ? `${maxHeight.t}px` : maxHeight.t};
          }
        `;
      }

      if (maxHeight.d) {
        response = `
          ${response}

          ${mediaQueries.desktop} {
            max-height: ${typeof maxHeight.d === 'number' ? `${maxHeight.d}px` : maxHeight.d};
          }
        `;
      }

      return response;
    } else {
      return `
        max-height: ${typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight};
      `;
    }
  }};

  iframe {
    width: 100%;
    height: 100%;
  }
`;

const SambaPlayer = ({
  externalVideoProviderIdOrUrl,

  startInSecond,
  onTimeUpdate,
  onEnd,

  width = '100%',
  height = '100%',
  maxWidth = 'inherit',
  maxHeight = 'inherit',

  ...props
}: VideoPlayerProps) => {
  const loadPlayer = useCallback(() => {
    window.Spalla = window.Spalla || {};

    window.Spalla.config = {
      element: `#samba-${videoId}`,
      stream: videoId,
      autoplay: true,
      // messages: true,
      // bar: true,
      // pip: true,
      // search: false,
      // muted: true,
      // audioplayer: false,
      // offline: false,
      // t: 20,
      // subtitlePosition: 'inner',
      // aid: '11111111-2222-4bb6-4444-000000000000',
      // debug: true,
    };

    window.addEventListener('spalla', (_event) => {
      const event = _event as unknown as ISpallaEvent;

      if (event.detail.event === 'stats' && onTimeUpdate) {
        if (onTimeUpdate) onTimeUpdate(event.detail.currentTime);
      }

      if (event.detail.event === 'ended' && onEnd) {
        onEnd(event.detail.duration);
      }
    });

    if (typeof window.Spalla.reloadSpallaConfig === 'undefined') return;

    window.Spalla.reloadSpallaConfig();
  }, [window.Spalla]);

  const videoId = extractVideoId(externalVideoProviderIdOrUrl);

  useEffect(() => {
    if (!window.Spalla) {
      const playerScript = document.createElement('script');

      playerScript.src = 'https://beyond.spalla.io/player/js/spalla-player.js';
      playerScript.charset = 'utf-8';

      playerScript.addEventListener('load', () => {
        return loadPlayer();
      });

      document.body.appendChild(playerScript);
    } else {
      loadPlayer();
    }
  }, [videoId]);

  return <PlayerWrapper id={`samba-${videoId}`} width={width} height={height} maxWidth={maxWidth} maxHeight={maxHeight} />;
};

export default SambaPlayer;

function extractVideoId(url: string): string | null {
  try {
    const parsed = new URL(url);
    // 1) tenta pegar o parâmetro "video"
    const fromParam = parsed.searchParams.get('video');
    if (fromParam && /^[0-9a-f]{32}$/i.test(fromParam)) {
      return fromParam;
    }
    // 2) tenta extrair do path (último segmento)
    const segments = parsed.pathname.split('/').filter(Boolean);
    const lastSegment = segments[segments.length - 1];
    if (/^[0-9a-f]{32}$/i.test(lastSegment)) {
      return lastSegment;
    }

    // 3) tenta pegar do param video
    const param = parsed.searchParams.get('video');
    if (param) {
      return param;
    }

    return null;
  } catch {
    // fallback genérico via regex
    const m = url.match(/([0-9a-f]{32})/i);
    return m ? m[1] : null;
  }
}
